// src/components/UI/common/EnhancedTableParts.jsx
/**
 * @file EnhancedTableParts.js
 * @description Shared components for table head and toolbar, used in all enhanced tables in RAVEN modules.
 * @author Yaofu Lee
 */

import React from "react";

import { Checkbox, Table, MetreaTooltip } from "@metrea-ui/v2"; // namespace Table API (Root/Header/Row/Cell/ColumnHeader)

import { mColor } from "src/utils/themeUtils";
import { showTesting } from "src/utils/debugUtils";
import { isDevTeam } from "src/utils/sessionUtils";

/**
 * A11y helper styles for screen-reader sort text.
 */
const visuallyHidden = {
	border: 0,
	clip: "rect(0 0 0 0)",
	height: 1,
	margin: -1,
	overflow: "hidden",
	padding: 0,
	position: "absolute",
	top: 20,
	width: 1,
};

const iconBoxStyle = {
	marginLeft: 4,
	width: "1em", // fixed width to prevent layout shift
	display: "inline-block",
	textAlign: "center",
	flex: "0 0 1em",
};

const getCheckboxValue = (next) =>
	typeof next === "boolean" || next === "indeterminate"
		? next
		: (next?.checked ?? next?.detail?.checked ?? next?.target?.checked ?? false);

const emitSelectAllChange = (onChange, next) => {
	onChange?.({
		target: { checked: next },
		preventDefault: () => {},
		stopPropagation: () => {},
	});
};

const renderSortLabelContent = ({ headCell, orderBy, order }) => (
	<>
		{headCell.label}
		{orderBy === headCell.id ? (
			<>
				<span aria-hidden="true" style={iconBoxStyle}>
					{order === "desc" ? "\u25BC" : "\u25B2"}
				</span>
				<span style={visuallyHidden}>
					{order === "desc" ? "sorted descending" : "sorted ascending"}
				</span>
			</>
		) : (
			// Placeholder keeps width identical when not the active sort.
			<span aria-hidden="true" style={{ ...iconBoxStyle, visibility: "hidden" }}>
				{"\u25B2"}
			</span>
		)}
	</>
);

/**
 * EnhancedTableHead
 *
 * Direct Metrea v2 table-head implementation used by the shared enhanced-table path.
 *
 * Props:
 * - numSelected: number
 * - order: 'asc' | 'desc'
 * - orderBy: string
 * - onSelectAllClick: (e) => void
 * - onRequestSort: (e, headCellId) => void
 * - rowCount: number
 * - headCells: Array<{ id, label, numeric?, align?, disablePadding?, disableSort? }>
 * - styles: optional CSS module object (class names)
 */
export const EnhancedTableHead = ({
	numSelected = 0,
	order,
	orderBy,
	onSelectAllClick,
	onRequestSort,
	rowCount = 0,
	headCells = [],
	styles,
}) => {
	const canShowDevCols = showTesting() && isDevTeam();
	const isIndeterminate = numSelected > 0 && numSelected < rowCount;
	const isChecked = rowCount > 0 && numSelected === rowCount;

	return (
		<Table.Header>
			<Table.Row style={{ backgroundColor: mColor.StonyBlue }}>
				{/* Dev/test select-all checkbox column */}
				{canShowDevCols && (
					<Table.ColumnHeader>
						<MetreaTooltip content="Select all records">
							<Checkbox.Root
								checked={isIndeterminate ? "indeterminate" : isChecked}
								onCheckedChange={(next) =>
									emitSelectAllChange(onSelectAllClick, getCheckboxValue(next) === true)
								}
								style={{ display: "inline-flex", alignItems: "center" }}
							>
								{/* Keep the hidden input in sync so form state and assistive tech see mixed state too. */}
								<Checkbox.HiddenInput aria-checked={isIndeterminate ? "mixed" : undefined} />
								<Checkbox.Control
									data-indeterminate={isIndeterminate ? "" : undefined}
									data-state={
										isIndeterminate
											? "indeterminate"
											: isChecked
												? "checked"
												: "unchecked"
									}
								/>
							</Checkbox.Root>
						</MetreaTooltip>
					</Table.ColumnHeader>
				)}

				{/* Map configured columns */}
				{headCells.map((headCell) => {
					const align = headCell.align ? headCell.align : headCell.numeric ? "end" : "start";
					const clickable = !headCell.disableSort;

					return (
						<Table.ColumnHeader
							key={headCell.id}
							textAlign={align}
							aria-sort={
								orderBy === headCell.id
									? order === "desc"
										? "descending"
										: "ascending"
									: "none"
							}
							className={styles?.tableHead}
						>
							{clickable ? (
								<button
									type="button"
									onClick={(e) => onRequestSort(e, headCell.id)}
									style={{
										display: "inline-flex",
										alignItems: "center",
										gap: "6px",
										background: "transparent",
										border: "none",
										padding: 0,
										cursor: "pointer",
										font: "inherit",
										fontWeight: 600,
									}}
								>
									{renderSortLabelContent({ headCell, orderBy, order })}
								</button>
							) : (
								<span
									style={{
										display: "inline-flex",
										alignItems: "center",
										gap: "6px",
										fontWeight: 600,
										opacity: 0.6,
									}}
								>
									{renderSortLabelContent({ headCell, orderBy, order })}
								</span>
							)}
						</Table.ColumnHeader>
					);
				})}
			</Table.Row>
		</Table.Header>
	);
};

/**
 * Minimal toolbar to show selection count (kept for compatibility).
 */
export const EnhancedTableToolbar = ({ numSelected = 0 }) => {
	return (
		<div style={{ paddingLeft: "8px", paddingRight: "8px" }}>
			{numSelected > 0 ? (
				<div style={{ fontWeight: 500 }}>
					{numSelected} {numSelected > 1 ? "records" : "record"} selected
				</div>
			) : (
				<div style={{ fontWeight: 600 }}>All records</div>
			)}
		</div>
	);
};
